/* eslint-disable react-hooks/exhaustive-deps */
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, TextField, Typography } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { all as getCountries } from 'country-codes-list';
import { isEmpty, sortBy } from 'lodash';
import validate from 'validate.js';
import { Firm } from 'api';
import { globalAction } from 'common/actions';
import {
  FIRM_FIELDS_CONSTRAINTS as constraints,
  CONTACT_INFORMATION,
  FIRM_SETTINGS,
  PHONE_NUMBER,
  WEBSITE,
} from 'common/constants/firms';
import { useStore } from 'common/store';
import type { Firm as FirmStoreModel } from 'common/types/firm';
import { UseStoreValues } from 'common/types/store';
import { WebsiteTextField } from 'components';
import ImageContainer from 'components/ImageContainer';
import { PixelCrop } from 'components/ImageContainer/utilities/types';
import { useSecondaryNavigationRegistration } from 'context/FirmBaseLayoutContext';
import { FirmAssetSignedUrlResponse, FirmHk, useFirmAssets, useImageUpload, useResponse } from 'services/hooks';
import { getObjectValue } from 'utilities';
import { CountryOption, FirmFormState, FirmFormStateValues, FirmImageFile, FirmImageFileResponse } from './FirmSettings.props';
import { classes, StyledGrid } from './FirmSettings.styles';
import { buildFirmSettingsNavigationItems } from './firmSettingsNavigation';
import handleOptionalFieldErrors from './utilities/handleOptionalFieldErrors';
import { syncFirmPreviewImageSrc } from './utilities/syncFirmPreviewImageSrc';

const FirmSettings = () => {
  const [storeValue, dispatch] = useStore() as unknown as UseStoreValues;
  const { firmInfo } = getObjectValue(storeValue);
  const { errorNotification } = useResponse();
  const { useUpdateFirmById } = FirmHk;
  const [, updateFirm] = useUpdateFirmById();

  const [iconImageSrc, setIconImageSrc] = useState<string | null>(null);
  const [logoImageSrc, setLogoImageSrc] = useState<string | null>(null);
  const [, setSelectedIconFile] = useState<File | null>(null);
  const [, setSelectedLogoFile] = useState<File | null>(null);
  const [croppedIconAreaPixels, setCroppedIconAreaPixels] = useState<PixelCrop>({} as PixelCrop);
  const latestIconCrop = useRef<PixelCrop>({} as PixelCrop);
  const [croppedLogoAreaPixels, setCroppedLogoAreaPixels] = useState<PixelCrop>({} as PixelCrop);
  const latestLogoCrop = useRef<PixelCrop>({} as PixelCrop);
  const [isSaving, setIsSaving] = useState(false);
  const uploadedIconUrlRef = useRef<string | null>(null);
  const uploadedLogoUrlRef = useRef<string | null>(null);

  const formStateRef = useRef<FirmFormState | null>(null);
  const [formState, setFormState] = useState<FirmFormState>({
    isValid: true,
    values: {
      address: '',
      country: '' as Firm.country,
      icon_url: '',
      logo_url: '',
      name: '',
      phone_number: '',
      state: '',
      street_address: '',
      website: '',
      zip_code: '',
    },
    errors: {},
  });

  const { getCroppedImage: getCroppedIconImage } = useImageUpload();
  const { getCroppedImage: getCroppedLogoImage } = useImageUpload();

  const { getFirmAssetSignedUrl, uploadImageFile } = useFirmAssets();

  const countries = useMemo(
    () =>
      getCountries().map(({ countryCode, countryNameEn }) => ({
        value: countryCode,
        label: countryNameEn,
      })),
    []
  );

  const handleCountryChange = useCallback((__event: ChangeEvent<object>, option: CountryOption | null) => {
    setFormState(prevFormState => ({
      ...prevFormState,
      values: {
        ...prevFormState.values,
        country: (option?.value ?? '') as Firm.country,
      },
    }));
  }, []);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    // Do not fire on the initial focus
    if (event.type) {
      event.persist();

      const tmpValues = {
        ...formState.values,
        [event.target.name]: event.target.value,
      };

      const errors = validate(tmpValues, constraints) ?? {};

      // This is a workaround for optional fields because ValidateJS
      // does not allow empty strings when you include a pattern or an URL
      if (
        errors?.phone_number &&
        handleOptionalFieldErrors({
          fieldName: PHONE_NUMBER,
          fieldValue: formState.values[PHONE_NUMBER],
          targetName: event.target.name,
          targetValue: event.target.value,
        })
      ) {
        delete errors.phone_number;
      }
      if (
        errors?.website &&
        handleOptionalFieldErrors({
          fieldName: WEBSITE,
          fieldValue: formState.values[WEBSITE],
          targetName: event.target.name,
          targetValue: event.target.value,
        })
      ) {
        delete errors.website;
      }
      setFormState(prevFormState => ({
        ...prevFormState,
        values: tmpValues,
        errors,
        isValid: isEmpty(errors),
      }));
    }
  };

  useEffect(() => {
    if (croppedIconAreaPixels) {
      latestIconCrop.current = croppedIconAreaPixels;
    }
  }, [croppedIconAreaPixels]);

  useEffect(() => {
    if (croppedLogoAreaPixels) {
      latestLogoCrop.current = croppedLogoAreaPixels;
    }
  }, [croppedLogoAreaPixels]);

  const uploadFirmImages = useCallback(
    async (files: FirmImageFile[], formData: FirmFormStateValues) => {
      const firmImages: Partial<Record<'icon_url' | 'logo_url', string | null>> = {};

      if (files[0].content && firmInfo?.id) {
        const iconSignedUrl = (await getFirmAssetSignedUrl(firmInfo.id, 'jpeg')) as FirmAssetSignedUrlResponse;
        if (iconSignedUrl.url) {
          const iconResponse = (await uploadImageFile(iconSignedUrl.url, files[0].content)) as FirmImageFileResponse;
          const cleanIconUrl = iconResponse.url.split('?')[0];
          firmImages.icon_url = cleanIconUrl;
        }
      } else if (!iconImageSrc) {
        firmImages.icon_url = null;
      }

      if (files[1].content && firmInfo?.id) {
        const iconSignedUrl = (await getFirmAssetSignedUrl(firmInfo.id, 'jpeg')) as FirmAssetSignedUrlResponse;
        if (iconSignedUrl.url) {
          const logoResult = (await uploadImageFile(iconSignedUrl.url, files[1].content)) as FirmAssetSignedUrlResponse;
          const cleanedLogoUrl = logoResult.url.split('?')[0];
          firmImages.logo_url = cleanedLogoUrl;
        }
      } else if (!logoImageSrc) {
        firmImages.logo_url = null;
      }
      if (firmInfo?.id) {
        await updateFirm(firmInfo.id, { ...formData, ...firmImages } as FirmStoreModel);
      }
      return firmImages;
    },
    [firmInfo, iconImageSrc, logoImageSrc, getFirmAssetSignedUrl, uploadImageFile, updateFirm]
  );

  const saveFirmSettings = useCallback(async () => {
    // Snapshot via ref before awaits / isSaving flips so crop+upload cannot pick up a hydrated reset.
    const formData = formStateRef.current?.values;
    if (!formData) {
      return;
    }

    dispatch(globalAction.showLoadingProgress(true));
    setIsSaving(true);
    try {
      const iconFile = await getCroppedIconImage(iconImageSrc ?? '', croppedIconAreaPixels);
      const logoFile = await getCroppedLogoImage(logoImageSrc ?? '', croppedLogoAreaPixels);

      // Keep original + user crop/zoom visible after save (no CDN preview swap)
      const uploadedUrls = await uploadFirmImages(
        [
          { content: iconFile, imageType: 'icon_url' },
          { content: logoFile, imageType: 'logo_url' },
        ],
        formData,
      );

      if (uploadedUrls.icon_url) {
        uploadedIconUrlRef.current = uploadedUrls.icon_url;
      } else if (uploadedUrls.icon_url === null) {
        uploadedIconUrlRef.current = null;
        setIconImageSrc(null);
      }

      if (uploadedUrls.logo_url) {
        uploadedLogoUrlRef.current = uploadedUrls.logo_url;
      } else if (uploadedUrls.logo_url === null) {
        uploadedLogoUrlRef.current = null;
        setLogoImageSrc(null);
      }
    } catch (err) {
      uploadedIconUrlRef.current = null;
      uploadedLogoUrlRef.current = null;
      errorNotification(err);
      dispatch(globalAction.showLoadingProgress(false));
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [
    dispatch,
    getCroppedIconImage,
    iconImageSrc,
    croppedIconAreaPixels,
    getCroppedLogoImage,
    logoImageSrc,
    croppedLogoAreaPixels,
    uploadFirmImages,
    errorNotification,
  ]);

  const firmSlug = firmInfo?.slug ?? '';

  const secondaryNavigation = useMemo(() => ({
    title: FIRM_SETTINGS,
    items: buildFirmSettingsNavigationItems(firmSlug, {
      dailyNavEnabled: Boolean(firmInfo?.daily_nav_enabled),
    }),
    actions: [{
      type: 'button' as const,
      key: 'firm-settings-save',
      label: 'Save',
      variant: 'positive' as const,
      onClick: saveFirmSettings,
      disabled: !formState.isValid,
    }],
  }), [firmSlug, firmInfo?.daily_nav_enabled, saveFirmSettings, formState.isValid]);

  useSecondaryNavigationRegistration(secondaryNavigation);

  const countryValue = useMemo(() => {
    if (countries && formState.values.country) {
      return countries.find(c => c.value === formState.values.country) ?? null;
    }
    return null;
  }, [formState.values.country, countries]);

  useEffect(() => {
    // isSaving is in the dep list so we rehydrate after save completes, but must not
    // rewrite in-progress edits when save starts — that overwrites the PUT payload.
    if (!firmInfo || isSaving) {
      return;
    }

    const firmAddress = firmInfo.street_address ?? firmInfo?.address ?? ''; // When creating a new firm we fill the "address" field
    const iconUrl = firmInfo?.icon_url ?? '';
    const logoUrl = firmInfo?.logo_url ?? '';

    setFormState(prevFormState => ({
      ...prevFormState,
      values: {
        ...prevFormState.values,
        address: firmAddress,
        country: (firmInfo.country ?? '') as Firm.country,
        icon_url: iconUrl,
        logo_url: logoUrl,
        name: firmInfo.name ?? '',
        phone_number: firmInfo.phone_number ?? '',
        state: firmInfo.state ?? '',
        street_address: firmAddress,
        website: firmInfo.website ?? '',
        zip_code: firmInfo.zip_code ?? '',
      },
    }));

    // Keep local crop preview stable after save (no CDN preview swap until URLs match).
    syncFirmPreviewImageSrc(uploadedIconUrlRef, iconUrl || null, setIconImageSrc);
    syncFirmPreviewImageSrc(uploadedLogoUrlRef, logoUrl || null, setLogoImageSrc);
  }, [firmInfo, isSaving]);

  useEffect(() => {
    formStateRef.current = formState;
  }, [formState]);

  return (
    <StyledGrid container spacing={3}>
      <Grid2 size={{ sm: 6 }}>
        <Typography variant="h6">Firm Information</Typography>
        <br />
        <Grid2 container spacing={4}>
          <Grid2 size={{ sm: 12 }}>
            <TextField
              className={classes.textField}
              label="Name"
              fullWidth
              variant="outlined"
              name="name"
              onChange={handleChange}
              type="text"
              inputProps={{ 'aria-label': 'name' }}
              id="name"
              helperText={formState.errors?.name?.join(', ')}
              value={formState.values.name}
              FormHelperTextProps={{
                className: classes.helperText,
              }}
            />
          </Grid2>
          <Grid2 size={{ sm: 12 }}>
            <TextField
              className={classes.textField}
              label="Street Address"
              fullWidth
              variant="outlined"
              name="street_address"
              onChange={handleChange}
              type="text"
              id="street_address"
              value={formState.values.street_address ?? ''}
              helperText={formState.errors?.street_address?.join(', ')}
              FormHelperTextProps={{
                className: classes.helperText,
              }}
            />
          </Grid2>
          <Grid2 size={{ sm: 12 }}>
            <Grid2 container spacing={2}>
              <Grid2 size={{ sm: 4 }}>
                <Autocomplete
                  id="country"
                  options={sortBy(countries, 'label')}
                  style={{ width: '100%' }}
                  autoHighlight
                  getOptionLabel={option => option.label}
                  onChange={handleCountryChange}
                  value={countryValue as CountryOption | undefined}
                  renderInput={params => (
                    <TextField
                      {...params}
                      className={classes.textField}
                      fullWidth
                      required
                      label="Country"
                      name="country"
                      variant="outlined"
                    />
                  )}
                  disableClearable
                />
              </Grid2>
              <Grid2 size={{ sm: 4 }}>
                <TextField
                  className={classes.textField}
                  label="State"
                  fullWidth
                  variant="outlined"
                  name="state"
                  onChange={handleChange}
                  type="text"
                  id="state"
                  value={formState.values.state ?? ''}
                  helperText={formState.errors?.state?.join(', ')}
                  FormHelperTextProps={{
                    className: classes.helperText,
                  }}
                />
              </Grid2>
              <Grid2 size={{ sm: 4 }}>
                <TextField
                  className={classes.textField}
                  label="Zip Code"
                  fullWidth
                  variant="outlined"
                  name="zip_code"
                  onChange={handleChange}
                  type="text"
                  id="zip_code"
                  value={formState.values.zip_code ?? ''}
                  helperText={formState.errors?.zip_code?.join(', ')}
                  FormHelperTextProps={{
                    className: classes.helperText,
                  }}
                />
              </Grid2>
            </Grid2>
          </Grid2>
        </Grid2>
      </Grid2>
      <Grid2 size={{ sm: 6 }}>
        <ImageContainer
          imageSrc={iconImageSrc}
          setImageSrc={setIconImageSrc}
          setSelectedFile={setSelectedIconFile}
          setCroppedAreaPixels={setCroppedIconAreaPixels}
          title="Firm icon"
          cropShape="rect"
        />
      </Grid2>
      <Grid2 size={{ sm: 6 }}>
        <Typography variant="h6">{CONTACT_INFORMATION}</Typography>
        <br />
        <Grid2 container spacing={4}>
          <Grid2 size={{ sm: 12 }}>
            <TextField
              className={classes.textField}
              label="Phone Number"
              fullWidth
              variant="outlined"
              name="phone_number"
              onChange={handleChange}
              type="text"
              id="phone_number"
              value={formState.values.phone_number ?? ''}
              helperText={formState.errors?.phone_number?.join(', ')}
              FormHelperTextProps={{
                className: classes.helperText,
              }}
            />
          </Grid2>
          <Grid2 size={{ sm: 12 }}>
            <WebsiteTextField
              className={classes.textField}
              label="Website"
              fullWidth
              variant="outlined"
              name="website"
              onChange={handleChange}
              type="text"
              inputProps={{ 'aria-label': 'website' }}
              id="website"
              helperText={formState.errors?.website?.join(', ')}
              value={formState.values.website ?? ''}
              FormHelperTextProps={{
                className: classes.helperText,
              }}
            />
          </Grid2>
        </Grid2>
      </Grid2>
      <Grid2 size={{ sm: 6 }} className={classes.firmLogoContainer}>
        <ImageContainer
          imageSrc={logoImageSrc}
          setImageSrc={setLogoImageSrc}
          setSelectedFile={setSelectedLogoFile}
          setCroppedAreaPixels={setCroppedLogoAreaPixels}
          title="Firm full logo"
          cropShape="rect"
          aspect={{ horizontal: 3.5, vertical: 1 }}
        />
      </Grid2>
    </StyledGrid>
  );
};

export default FirmSettings;
